import { DotLegalDialog, DotLegalHeader, DotLegalLink, DotLegalPrettyScrollbar } from "@dotlegal/dotlegal-ui-components";
import { Box } from "@mui/material";
import { useTranslation } from "@localization/useTranslation.ts";
import DotLegalCollapse from "@common/components/dotLegalCollapse/DotLegalCollapse.tsx";
import { DeletionBlockerGroup } from "./DeleteBlockedDialog.types.ts";
import { useDeleteBlockedDialog } from "./DeleteBlockedDialog.hooks.ts";

export interface IDeleteBlockedDialogProps {
    header: string;
    description: string;
    /**
     * Advice shown as part of the introduction, such as offering a way to keep the item instead of deleting it.
     */
    advice?: string;
    blockers: Array<DeletionBlockerGroup>;
    blockerTranslationKeys: Record<number, string>;
    /**
     * Sentence keys for the "N you do not have access to" line, per blocker type. Only access filtered
     * blockers ever need one; the rest fall back to the type-agnostic sentence.
     */
    blockerNoAccessTranslationKeys?: Partial<Record<number, string>>;
    onDialogClose: () => void;
}

function DeleteBlockedDialog(props: IDeleteBlockedDialogProps) {
    const { translateString } = useTranslation();
    const { expandedType, toggleExpanded, getBlockerName, getItemUrl, isTruncated, getNoAccessText } = useDeleteBlockedDialog(
        props.blockers,
        props.blockerTranslationKeys,
        props.blockerNoAccessTranslationKeys
    );

    return (
        <DotLegalDialog
            buttonOkText={translateString("close")}
            open
            onDialogClose={props.onDialogClose}
            onOkClick={props.onDialogClose}
            size={"md"}
            header={props.header}
        >
            <Box sx={{ marginRight: -2 }}>
                <DotLegalPrettyScrollbar>
                    <Box sx={{ maxHeight: 525, paddingRight: 1 }}>
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                            <DotLegalHeader headerStyle={"small"} fontWeight={400} center marginBottom={0}>
                                {props.description}
                            </DotLegalHeader>

                            {props.advice && (
                                <DotLegalHeader headerStyle={"small"} fontWeight={400} center marginBottom={2}>
                                    {props.advice}
                                </DotLegalHeader>
                            )}
                        </Box>

                        {props.blockers.map((group) => (
                            <DotLegalCollapse
                                key={group.type}
                                isExpanded={expandedType === group.type}
                                onExpandClick={() => toggleExpanded(group.type)}
                                collapseName={getBlockerName(group)}
                                persistCollapseName
                                leftAlign
                                isCollapseNameBold
                                sx={{ marginBottom: 1, borderWidth: "1px" }}
                            >
                                <>
                                    {group.items.map((item) => {
                                        const url = getItemUrl(group, item);
                                        return (
                                            <DotLegalHeader key={item.id} marginBottom={1} headerStyle={"extraSmall"} fontWeight={400}>
                                                {url ? (
                                                    <DotLegalLink bold to={url} linkColor={"primary"}>
                                                        {item.name}
                                                    </DotLegalLink>
                                                ) : (
                                                    item.name
                                                )}
                                            </DotLegalHeader>
                                        );
                                    })}
                                    {isTruncated(group) && (
                                        <DotLegalHeader headerStyle={"extraSmall"} fontWeight={400} marginBottom={1}>
                                            {translateString("deletionBlockerShowingFirst", { count: group.items.length, total: group.totalCount })}
                                        </DotLegalHeader>
                                    )}
                                    {group.noAccessCount > 0 && (
                                        <DotLegalHeader headerStyle={"extraSmall"} fontWeight={400} marginBottom={1}>
                                            {getNoAccessText(group)}
                                        </DotLegalHeader>
                                    )}
                                </>
                            </DotLegalCollapse>
                        ))}
                    </Box>
                </DotLegalPrettyScrollbar>
            </Box>
        </DotLegalDialog>
    );
}

export default DeleteBlockedDialog;
